import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeftIcon, BookOpenIcon, CheckIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import CustomDialog from "../modals/CustomDialog/CustomDialog";
import { Button } from "../forms/Buttons/Button";
import AsyncSearchSelect from "../forms/Select/AsyncSearchSelect";
import { isBlankHtml, RichText } from "../KaTeXComponent/RichText";
import { createPassage, deletePassage, getPassages, setPassageQuestions, uploadPassageBody } from "../../services/passageService";
import { focusRing, linkButtonClass, Spinner } from "../../pages/Exams/TestBuilder/ui";
import PassageEditor, { emptyPassageForm } from "./PassageEditor";
import { isPassageDetail, passageFormErrors, quietDialogButton } from "./PassageDialog";
import PassageLockNotice, { passageBooklets } from "./PassageLock";
import { passageErrorMessage } from "./passageErrors";
import QuestionPicker from "./QuestionPicker";
import usePassage, { forgetPassage, rememberPassage } from "./usePassage";
import { UserContext } from "../../utils/contexts/UserContext";
import { isAdminUser } from "../../utils/folders";
import { getApiErrorCode } from "../../utils/apiError";
import { BookletLinks, linksBooklets, lockingBooklets } from "../../pages/QuestionPage/review/bookletLock";

const listOf = (res) => (Array.isArray(res) ? res : res?.results || []);
const subjectUuidOf = (subject) => (typeof subject === "string" ? subject : subject?.uuid) || null;

function Steps({ step }) {
  const [t] = useTranslation();
  const items = [t("passages.context.step_text"), t("passages.context.step_questions")];
  return (
    <ol className="flex items-center gap-2 text-sm">
      {items.map((label, i) => {
        const n = i + 1;
        const done = n < step;
        const current = n === step;
        return (
          <li key={label} className="flex items-center gap-2" aria-current={current ? "step" : undefined}>
            {i > 0 && <span className="h-px w-6 bg-slate-200" aria-hidden="true" />}
            <span
              className={`grid h-6 w-6 place-items-center rounded-full text-xs font-semibold ${
                current ? "bg-blue-600 text-white" : done ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"
              }`}
            >
              {done ? <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" /> : n}
            </span>
            <span className={current ? "font-medium text-slate-900" : "text-slate-500"}>{label}</span>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * "Context question" in the test builder: a passage (new, or one from the bank) and the bank questions
 * based on it. The passage is saved in the bank and the questions linked to it here; adding them to the
 * test is left to the builder: onDone({passage: {uuid, name}, questions}) with the questions in passage
 * order, each with `passage` set (questions already in the test included). A passage's questions live in its
 * folder, so the folder is chosen with the text (step 1) and only its questions are offered (step 2).
 * A bank passage that active BTS booklets print (its detail's `booklets`) takes no new question: it says so, and
 * goes into the test with the questions it has.
 */
export default function ContextQuestionDialog({ isOpen, onClose, inTest, subject = null, onDone }) {
  const [t] = useTranslation();
  const { user } = useContext(UserContext);
  const perms = user?.perms || [];
  // Without the global rights a new passage goes into a folder's subject.
  const requireFolder = !perms.includes("create_question") && !perms.includes("create_exam") && !isAdminUser(user);
  const [step, setStep] = useState(1);
  const [tab, setTab] = useState("new");
  const [form, setForm] = useState(emptyPassageForm);
  const [errors, setErrors] = useState({});
  const [editorKey, setEditorKey] = useState(0);
  const [existing, setExisting] = useState(null); // {value, label} of a bank passage
  const [created, setCreated] = useState(null); // {uuid, name} once a new passage was saved
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false); // a picture is being uploaded into the text
  // {message, booklets, note}: the text, the links of the active BTS booklets that refused the link (for an admin, who
  // returns them to draft) and what became of the passage, said after the links.
  const [error, setError] = useState(null);

  const chosenUuid = tab === "existing" ? existing?.value || null : created?.uuid || null;
  const bank = usePassage(tab === "existing" ? existing?.value : null);
  const members = useMemo(() => (tab === "existing" ? bank.data?.questions || [] : []), [tab, bank.data]);
  const lockedIds = useMemo(() => new Set(members.map((q) => q.uuid)), [members]);
  // The bank passage is printed: no question joins it (set_questions would answer question_in_booklet).
  const printed = tab === "existing" ? passageBooklets(bank.data) : [];
  const lockNotice = printed.length > 0 && (
    <PassageLockNotice booklets={printed} lock="passage" extra={t("passages.lock.context_existing")} />
  );

  useEffect(() => {
    if (!isOpen) return;
    setStep(1);
    setTab("new");
    setForm({ ...emptyPassageForm, subjectUuid: subjectUuidOf(subject) });
    setErrors({});
    setEditorKey((k) => k + 1);
    setExisting(null);
    setCreated(null);
    setSelected([]);
    setError(null);
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = step > 1 || !!form.name.trim() || !isBlankHtml(form.body) || !!form.file || !!existing;
  const close = async () => {
    if (saving) return;
    // A passage saved here whose questions did not get linked would stay in the bank empty: closing removes it.
    if (created) {
      if (!window.confirm(t("passages.context.discard_created", { name: created.name }))) return;
      await deletePassage(created.uuid).then(() => forgetPassage(created.uuid), () => {});
      onClose();
      return;
    }
    if (dirty && !window.confirm(t("passages.dialog.discard_confirm"))) return;
    onClose();
  };

  const update = (patch) => {
    const { bodyEdited, ...rest } = patch;
    setForm((prev) => ({ ...prev, ...rest }));
    setErrors({});
  };

  const fetchPassages = useCallback(
    (search) => getPassages({ params: { search: search || undefined, page_size: 20, ordering: "-updated" } }).then(listOf),
    []
  );

  const next = () => {
    if (uploading) return;
    setError(null);
    if (tab === "new" && !created) {
      const problems = passageFormErrors(t, form, { requireFolder });
      setErrors(problems);
      if (Object.keys(problems).length) return;
    } else if (!existing || !bank.data) {
      setError({ message: t("passages.context.need_text") });
      return;
    }
    // A bank passage keeps its questions first; new picks go after them.
    setSelected((prev) => (tab === "existing" ? [...members, ...prev.filter((q) => !lockedIds.has(q.uuid))] : prev));
    setStep(2);
  };

  const finish = async () => {
    if (uploading) return;
    if (!selected.length) {
      setError({ message: t("passages.context.need_questions") });
      return;
    }
    setSaving(true);
    setError(null);
    let passage = tab === "existing" ? { uuid: existing.value, name: bank.data?.name || existing.label } : created;
    let justCreated = false;
    let linking = false; // the questions are being linked (set_questions)
    try {
      let detail = null;
      if (!passage) {
        detail = await createPassage({
          name: form.name.trim(),
          subject_uuid: form.subjectUuid || null,
          folder_uuid: form.folderUuid || null,
          body: form.file ? "" : form.body,
        });
        passage = { uuid: detail.uuid, name: detail.name };
        justCreated = true;
        setCreated(passage);
      }
      if (tab === "new" && form.file) {
        try {
          detail = await uploadPassageBody(passage.uuid, form.file);
        } catch (err) {
          // The passage made a moment ago for this file goes away again, so the form can be fixed and resent.
          if (justCreated) {
            const orphan = passage.uuid;
            await deletePassage(orphan).then(
              () => {
                passage = null;
                setCreated(null);
              },
              () => {}
            );
          }
          throw err;
        }
        setForm((prev) => ({ ...prev, file: null }));
      }
      const ids = selected.map((q) => q.uuid);
      const alreadyLinked = tab === "existing" && ids.length === members.length && ids.every((id, i) => id === members[i].uuid);
      if (!alreadyLinked) {
        linking = true;
        detail = await setPassageQuestions(passage.uuid, ids);
      }
      if (isPassageDetail(detail)) rememberPassage(detail);
      else forgetPassage(passage.uuid);
      onDone({ passage, questions: selected.map((q) => ({ ...q, passage })) });
      onClose();
    } catch (err) {
      // An admin returns the BTS booklets that refused the link to draft: the text asks them to, with the links.
      const booklets = getApiErrorCode(err) === "question_in_booklet" ? lockingBooklets(err) : [];
      const linked = linksBooklets(user, booklets);
      setError({
        message: passageErrorMessage(t, err, "passages.dialog.save_failed", [...members, ...selected], { linked, linking }),
        booklets: linked ? booklets : [],
        // The passage exists now: a retry links the questions to it instead of creating another one.
        note: passage && tab === "new" ? t("passages.context.saved_partial") : "",
      });
    } finally {
      setSaving(false);
    }
  };

  const tabClass = (active) =>
    `rounded-lg px-3 py-1.5 text-sm font-medium transition duration-200 max-md:min-h-[2.75rem] ${focusRing} ${
      active ? "bg-white text-slate-900 shadow-sm ring-1 ring-slate-200" : "text-slate-600 hover:text-slate-900"
    }`;

  return (
    <CustomDialog isOpen={isOpen} closeModal={close} headerText={t("passages.context.title")} maxWidth="max-w-6xl">
      <div className="mt-3 space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Steps step={step} />
          <p className="max-w-xl text-sm text-slate-500">{t("passages.context.intro")}</p>
        </div>

        {step === 1 ? (
          <>
            {!created && (
              <div role="tablist" className="inline-flex gap-1 rounded-xl bg-slate-100 p-1">
                <button
                  type="button"
                  role="tab"
                  aria-selected={tab === "new"}
                  onClick={() => {
                    // The bank passage's own questions belong to it, not to a new passage; free picks stay.
                    setSelected((prev) => prev.filter((q) => !lockedIds.has(q.uuid)));
                    setTab("new");
                  }}
                  className={tabClass(tab === "new")}
                >
                  {t("passages.context.tab_new")}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={tab === "existing"}
                  onClick={() => setTab("existing")}
                  className={tabClass(tab === "existing")}
                >
                  {t("passages.context.tab_existing")}
                </button>
              </div>
            )}
            {tab === "new" ? (
              <>
                <PassageEditor
                  value={form}
                  onChange={update}
                  editorKey={editorKey}
                  errors={errors}
                  disabled={saving || !!created}
                  onUploadingChange={setUploading}
                  allowNoFolder={!requireFolder}
                />
                <p className="text-xs text-slate-500">
                  {created ? t("passages.context.created_note") : t("passages.context.folder_hint")}
                </p>
              </>
            ) : (
              <div className="space-y-3">
                <AsyncSearchSelect
                  label={t("passages.context.existing_search")}
                  placeholder={t("passages.search")}
                  fetchOptions={fetchPassages}
                  value={existing}
                  onChange={(option) => {
                    // Its questions are replaced as a whole on save: read them fresh, not from a cached detail.
                    if (option?.value) forgetPassage(option.value);
                    setExisting(option || null);
                    setSelected([]);
                    setError(null);
                  }}
                />
                {existing && (
                  <div className="rounded-xl border border-indigo-100 bg-indigo-50/50">
                    <div className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm font-medium text-indigo-900">
                      <BookOpenIcon className="h-4 w-4 text-indigo-600" aria-hidden="true" />
                      {existing.label}
                      {/* A printed passage takes no new question: only how many it has. */}
                      {bank.data && (
                        <span className="font-normal text-indigo-700">
                          ·{" "}
                          {printed.length
                            ? t("question_list.count", { count: members.length })
                            : t("passages.context.existing_members", { count: members.length })}
                        </span>
                      )}
                    </div>
                    <div className="border-t border-indigo-100 bg-white px-4 py-3">
                      {bank.loading ? (
                        <Spinner />
                      ) : bank.error ? (
                        <p role="alert" className="flex flex-wrap items-center gap-2 text-sm text-red-700">
                          <ExclamationTriangleIcon className="h-4 w-4" aria-hidden="true" />
                          {t("passages.panel.load_failed")}
                          <button type="button" onClick={bank.retry} className={linkButtonClass}>
                            {t("passages.panel.retry")}
                          </button>
                        </p>
                      ) : (
                        <div className="passage-content max-h-[22rem] overflow-y-auto text-sm leading-relaxed text-slate-800 [&_img]:max-h-72 [&_img]:!w-auto">
                          <RichText html={bank.data?.body} dollarMath={false} />
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {lockNotice}
              </div>
            )}
          </>
        ) : (
          <div className="space-y-3">
            {lockNotice || <p className="text-sm text-slate-600">{t("passages.context.questions_hint")}</p>}
            <QuestionPicker
              selected={selected}
              onChange={(nextSelected) => {
                setSelected(nextSelected);
                setError(null);
              }}
              lockedIds={lockedIds}
              passageUuid={chosenUuid}
              subject={tab === "new" ? form.subjectUuid : bank.data?.subject?.uuid || subjectUuidOf(subject)}
              folder={(tab === "new" ? form.folderUuid : bank.data?.folder?.uuid) || "null"}
              inTest={inTest}
              canAdd={!printed.length}
            />
          </div>
        )}

        {error && (
          <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {error.message}
            <BookletLinks booklets={error.booklets} />
            {error.note && <span className="mt-1 block">{error.note}</span>}
          </p>
        )}
      </div>

      {/* One main button; Cancel and Back stay quiet. Phones: Back and the main button share a row, Cancel goes
          under them on step 2. Once the passage is saved its text can't change here any more, but step 1 stays
          reachable. */}
      <div className="mt-6 flex flex-wrap items-center justify-end gap-2">
        <button
          type="button"
          onClick={close}
          disabled={saving}
          className={`${quietDialogButton} md:mr-auto ${step === 2 ? "max-md:order-last max-md:w-full" : ""}`}
        >
          {t("buttons.cancel")}
        </button>
        {step === 2 && (
          <button type="button" onClick={() => setStep(1)} disabled={saving} className={quietDialogButton}>
            <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
            {t("buttons.back")}
          </button>
        )}
        {step === 1 ? (
          <Button type="button" color="primary" text={t("buttons.next")} onClick={next} disabled={uploading} className="max-md:h-11 max-md:flex-1" />
        ) : (
          <Button
            type="button"
            color="primary"
            text={t("passages.context.add")}
            onClick={finish}
            loading={saving}
            disabled={uploading}
            className="max-md:h-11 max-md:flex-1"
          />
        )}
      </div>
    </CustomDialog>
  );
}
